import { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { gql } from '@apollo/client';
import { useGraphql } from 'utils/hooks/useGraphql';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import upperFirst from 'lodash/upperFirst';

import { DESCENDING, TABLE_GQL_SORT_DIRECTION_MAP } from 'constants/sortDirections';
import { LIST_TYPES } from 'constants/listTypes';
import { SecondaryButton, RefreshButton } from 'ui/button';
import { spacing, WebLink, Body1 } from 'ui/theme';
import { addNegativeToast } from 'modules/toastContainer';
import { updateNodeData } from 'modules/workflows/ducks';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { removeEmptyDeep } from 'modules/workflows/components/NodeEditor/utils';
import { NodeErrorBanner } from '../NodeErrorBanner';
import { TableSelect, PAGE_SIZE_OPTIONS } from '../TableSelect';
// import { AddOrRemoveContactNodeData, AddOrRemoveContactEditorProps } from './types';
import { UPDATE_CONTACT_COLUMNS } from './contactAction.constants';
import {
  ListTitleBar,
  ListTitle,
  SelectedListInfo,
  StyledOpenNewWindow16,
  TableHeaderBar,
} from './contactAction.styles';

const VISIBLE_LIST_TYPES = [LIST_TYPES.STANDARD, LIST_TYPES.SUPPRESSION];

export const CONTACT_NODE_LISTS_QUERY = gql`
  query getLists(
    $filterInfo: ListFilterInfoInput
    $pagination: Pagination
    $search: String
    $sort: Sort
  ) {
    lists(filterInfo: $filterInfo, pagination: $pagination, sort: $sort, search: $search) {
      results {
        id
        name
        updatedAt
      }
      info {
        count
      }
    }
  }
`;

export const getContactListEditorNodeDataToBeCompared = (
  data /*: AddOrRemoveContactNodeData */,
) => {
  const cleanedData = removeEmptyDeep(data);
  return { emailListId: cleanedData?.emailListId ?? undefined };
};

export const CONTACT_LIST_EDITOR_TEST_ID = 'contact-list-editor';

// export interface ContactListEditorProps extends AddOrRemoveContactEditorProps {
//   /** Data test tag attached to node editor component */
//   dataTest?: string;
//   /** The title of the editor */
//   title?: string;
//   /** Show the button that allows you to create a list */
//   isCreateListButtonShown?: boolean;
// }

/**
 * ContactListEditor
 * @param {ContactListEditorProps} props
 */
export const ContactListEditor = (
  {
    dataTest = CONTACT_LIST_EDITOR_TEST_ID,
    nodeData = {},
    nodeId,
    title = '',
    isCreateListButtonShown = false,
    ...rest
  } /*: ContactListEditorProps */,
) => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [emailListId, setEmailListId] = useState(nodeData.emailListId);

  // initialize selectedRow using nodeData
  // because graphql may not have it due to pagination issues
  const [selectedRow, setSelectedRow] = useState({
    name: nodeData?.resource?.name,
    id: nodeData?.emailListId,
  });

  const [limit, setLimit] = useState(PAGE_SIZE_OPTIONS[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortDirection, setSortDirection] = useState(DESCENDING);
  const [total, setTotal] = useState(0);

  /** Error Validation */
  const [isErrorBannerShown, setIsErrorBannerShown] = useState(false);

  const getOffset = (opts = {}) => {
    const { currentPage: pg = currentPage, limit: lt = limit } = opts;
    return (pg - 1) * lt;
  };

  const getSortVariables = (opts = {}) => {
    const { sortBy: by = sortBy, sortDirection: dir = sortDirection } = opts;
    return {
      sortBy: upperFirst(by),
      sortDirection: TABLE_GQL_SORT_DIRECTION_MAP[dir] || DESCENDING,
    };
  };

  const updateSelectedRow = (lists = rows, id = emailListId) => {
    const row = find(lists, { id });

    // For "Update Node" to work correctly, we need to maintain a
    // reference to a selected row even if it is not returned by gql
    // (which might happen due to pagination change)
    if (row) {
      setSelectedRow(row);
    }
  };

  const onListFetchComplete = (data) => {
    if (!data) return;

    const { lists = {} } = data;
    const { results = [], info = {} } = lists;

    setRows(results);
    setTotal(info?.count || 0);

    if (results?.length && emailListId) {
      updateSelectedRow(results, emailListId);
    }
  };

  const getQueryVariables = (overrides = {}) => {
    const {
      currentPage: pg = currentPage,
      limit: lt = limit,
      search: srch = search,
      sortBy: by = sortBy,
      sortDirection: dir = sortDirection,
    } = overrides;

    return {
      pagination: {
        limit: lt,
        offset: getOffset({ currentPage: pg, limit: lt }),
      },
      search: srch || null,
      sort: getSortVariables({ sortBy: by, sortDirection: dir }),
      filterInfo: {
        listType: VISIBLE_LIST_TYPES,
      },
    };
  };

  const { loading: isLoading, refetch } = useGraphql(CONTACT_NODE_LISTS_QUERY, {
    /**
     * 'cache-first' will *always* return the cached value, even if it has been
     * updated.
     * 'cache-and-network' will return the updated value if it exists. It
     * seems to be the best way to balance performance and accurate data
     *
     * https://www.apollographql.com/docs/react/data/queries/#supported-fetch-policies
     */
    fetchPolicy: 'cache-and-network',
    // Allow for partial use of the cache on refresh
    partialRefetch: true,
    // Show all errors from graphql.  Allows for rendering of partial results.
    errorPolicy: 'all',
    // Allows us to know if the network status has changed
    notifyOnNetworkStatusChange: true,
    // Receives partial data from the cache
    returnPartialData: true,
    variables: getQueryVariables(),
    onError: (err) => dispatch(addNegativeToast(err?.message)),
    onCompleted: onListFetchComplete,
  });

  const updateSearch = (e) => {
    if (e !== search) {
      setCurrentPage(1);
      refetch(getQueryVariables({ currentPage: 1, search: e }));
    }
  };

  const debouncedSearchUpdate = debounce(updateSearch, 300, {
    leading: false,
    trailing: true,
    maxWait: 1000,
  });

  const handleSearch = (e) => {
    // Sets the value that you see in the search input box
    setSearch(e);
    // Runs an update which is debounced for performance sake
    debouncedSearchUpdate(e);
  };

  const getUpdatedNodeData = () => ({
    ...nodeData,
    emailListId,
    resource: selectedRow,
  });

  /** "Update Node" pressed */
  const handleUpdate = useCallback(
    (requiresValidation = true) => {
      if (selectedRow || !requiresValidation) {
        dispatch(updateNodeData(getUpdatedNodeData(), nodeId));
      } else {
        // don't update or close node if error occurs
        setIsErrorBannerShown(true);
      }
    },
    [selectedRow?.id, selectedRow?.name, nodeId, emailListId],
  );

  const initialNodeData = useMemo(
    () => getContactListEditorNodeDataToBeCompared(nodeData),
    [nodeData],
  );
  const updatedNodeData = getContactListEditorNodeDataToBeCompared(getUpdatedNodeData());

  return (
    <NodeEditor
      {...rest}
      dataTest={dataTest}
      onUpdate={handleUpdate}
      /** These are used to check if node has changes */
      initialNodeData={initialNodeData}
      updatedNodeData={updatedNodeData}
    >
      <ListTitleBar>
        <ListTitle as="h2">{title}</ListTitle>
        {selectedRow?.id && (
          <SelectedListInfo as="h3">
            {selectedRow?.name || null} (ID {selectedRow?.id})
          </SelectedListInfo>
        )}
      </ListTitleBar>
      {isErrorBannerShown && <NodeErrorBanner />}
      <TableSelect
        data-test="contact-list-table"
        columns={UPDATE_CONTACT_COLUMNS}
        currentPage={currentPage}
        isLoading={isLoading}
        itemsPerPage={limit}
        rows={rows}
        search={search}
        selectedId={selectedRow?.id}
        sortDirection={sortDirection}
        sortBy={sortBy}
        total={total}
        onChange={(e) => {
          setSelectedRow(e);
          setEmailListId(e?.id);
        }}
        onPaginationChange={({ pageSize, page }) => {
          setCurrentPage(page);
          setLimit(pageSize);
          refetch(getQueryVariables({ currentPage: page, limit: pageSize }));
        }}
        onSearch={handleSearch}
        onSort={(e) => {
          setSortBy(e.sortBy);
          setSortDirection(e.sortDirection);
          refetch(getQueryVariables(e));
        }}
        HeaderBarChildren={
          <TableHeaderBar>
            {isCreateListButtonShown && (
              <SecondaryButton
                as="a"
                href="/lists/import"
                rel="noreferrer noopener"
                // For some reason, all style is lost when using styled components
                // Adding it directly is the workaround :(
                style={{ marginRight: spacing.quarter }}
                target="_blank"
              >
                <StyledOpenNewWindow16 />
                Create list
              </SecondaryButton>
            )}
            <RefreshButton onClick={() => refetch(getQueryVariables())} isDisabled={isLoading} />
          </TableHeaderBar>
        }
        NoRowsCmp={
          <Body1>
            No lists exist. &nbsp;
            <WebLink href="/lists" target="_blank">
              Create a new list
            </WebLink>
          </Body1>
        }
      />
    </NodeEditor>
  );
};
